import { Box, Paper, Skeleton, Typography } from "@mui/material";
import { useState } from "react";

import MarkdownRenderer from "../../../components/MarkdownRenderer";
import { ViewerArtifacts } from "../../../data/queries/viewer";
import { PROCESS_DECISION_DO_NOT_PROCESS } from "../../../types/risk-analysis";
import { hasRiskAnalysisData } from "../../../utils/riskAnalysis";
import BetaBanner from "./potential-risk/BetaBanner";
import { RISK_DIMENSIONS } from "./potential-risk/constants";
import OverallRiskCard from "./potential-risk/OverallRiskCard";
import RiskDimensionCard from "./potential-risk/RiskDimensionCard";

interface PotentialRiskTabProps {
  data: ViewerArtifacts | null;
  isLoading: boolean;
}

const SKELETON_DIMENSIONS = [1, 2, 3, 4] as const;

const NoDataMessage = () => (
  <Box>
    <Paper elevation={0} sx={{ p: 2, mb: 2 }}>
      <Typography variant="h6" gutterBottom>
        Potential Risk
      </Typography>
      <Typography variant="body1" color="text.secondary">
        No risk analysis data available for this interaction.
      </Typography>
    </Paper>
  </Box>
);

const LoadingSkeleton = () => (
  <Box sx={{ width: "100%" }}>
    <Skeleton variant="rectangular" height={48} sx={{ borderRadius: "8px", mb: 3 }} />
    <Skeleton variant="rectangular" height={200} sx={{ borderRadius: "8px", mb: 3 }} />
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "1fr",
          md: "repeat(2, 1fr)",
          xl: "repeat(4, 1fr)"
        },
        gap: 2
      }}
    >
      {SKELETON_DIMENSIONS.map((key) => (
        <Skeleton
          key={key}
          variant="rectangular"
          height={180}
          sx={{ borderRadius: "8px" }}
        />
      ))}
    </Box>
  </Box>
);

export const PotentialRiskTab = ({ data, isLoading }: PotentialRiskTabProps) => {
  const [isBannerExpanded, setIsBannerExpanded] = useState(false);

  if (isLoading) {
    return <LoadingSkeleton />;
  }

  if (!data?.riskAnalysis) {
    return <NoDataMessage />;
  }

  const riskContent = data.riskAnalysisNormalized;

  if (!riskContent) {
    return <NoDataMessage />;
  }

  if (riskContent.process_decision === PROCESS_DECISION_DO_NOT_PROCESS) {
    return (
      <Box>
        <Paper elevation={0} sx={{ p: 2, mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            Potential Risk
          </Typography>
          {riskContent.reason_description && (
            <MarkdownRenderer content={riskContent.reason_description} />
          )}
        </Paper>
      </Box>
    );
  }

  const { riskAnalysis } = riskContent;

  if (!hasRiskAnalysisData(riskAnalysis)) {
    return <NoDataMessage />;
  }

  return (
    <Box>
      <BetaBanner
        isExpanded={isBannerExpanded}
        onToggle={() => setIsBannerExpanded((prev) => !prev)}
      />

      <OverallRiskCard
        riskAnalysis={riskAnalysis}
        agentPerformance={riskContent.agentPerformance}
      />

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            md: "repeat(2, 1fr)",
            xl: "repeat(4, 1fr)"
          },
          gap: 2,
          alignItems: "start"
        }}
      >
        {RISK_DIMENSIONS.map((dimension) => (
          <RiskDimensionCard
            key={dimension.id}
            dimensionId={dimension.id}
            name={dimension.name}
            data={riskAnalysis.riskTypes[dimension.riskTypeKey]}
          />
        ))}
      </Box>
    </Box>
  );
};

export default PotentialRiskTab;
